import { Box, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

interface HeadingCompassProps {
  heading: number
}

const cx = 60
const cy = 60

// Counter-clockwise convention: 0°=Front/top, +90°=Left, -90°=Right, 180°=Back
const pointAt = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180
  return { x: cx - r * Math.sin(a), y: cy - r * Math.cos(a) }
}

const HeadingCompass = ({ heading }: HeadingCompassProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const tip = pointAt(heading, 38)

  const ringColor = theme.palette.neutral[600]
  const labelColor = theme.palette.text.primary
  const accentColor = theme.palette.neutral[900]
  const axisColor = theme.palette.neutral[600]

  const outerR = 50
  const innerR = 34

  // Cross axes endpoints
  const axisTop = pointAt(0, outerR)
  const axisBottom = pointAt(180, outerR)
  const axisLeft = pointAt(90, outerR)
  const axisRight = pointAt(-90, outerR)

  // 8 tick marks at every 45°
  const tickAngles = [0, 45, 90, 135, 180, -135, -90, -45]
  const ticks = tickAngles.map((deg) => ({
    deg,
    inner: pointAt(deg, 46),
    outer: pointAt(deg, outerR),
  }))

  // Diagonal label positions (outside outer ring)
  const diagLabels = [
    { deg: 45, label: "45°", anchor: "end" },
    { deg: 135, label: "135°", anchor: "end" },
    { deg: -135, label: "-135°", anchor: "start" },
    { deg: -45, label: "-45°", anchor: "start" },
  ]
  const diagLabelR = 58

  // Cardinal label positions (outside ring)
  const cardinalOffset = 66
  const cardinals = [
    { deg: 0, nameKey: "FRONT", angleLabel: "0°", anchor: "middle", dy: -3 },
    { deg: 90, nameKey: "HEADING_LEFT", angleLabel: "90°", anchor: "end", dy: 0 },
    { deg: 180, nameKey: "BACK", angleLabel: "180°", anchor: "middle", dy: 3 },
    { deg: -90, nameKey: "HEADING_RIGHT", angleLabel: "-90°", anchor: "start", dy: 0 },
  ]

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg
        viewBox="-42 -42 204 204"
        width="200"
        height="200"
        style={{ display: "block" }}
      >
        <defs>
          <marker
            id="compass-arrowhead"
            markerWidth={10}
            markerHeight={10}
            refX={7}
            refY={5}
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={accentColor} />
          </marker>
        </defs>

        {/* Cross axes */}
        <line
          x1={axisLeft.x}
          y1={axisLeft.y}
          x2={axisRight.x}
          y2={axisRight.y}
          stroke={axisColor}
          strokeWidth={0.75}
          opacity={0.3}
        />
        <line
          x1={axisTop.x}
          y1={axisTop.y}
          x2={axisBottom.x}
          y2={axisBottom.y}
          stroke={axisColor}
          strokeWidth={0.75}
          opacity={0.3}
        />

        {/* Rings */}
        <circle
          cx={cx}
          cy={cy}
          r={outerR}
          fill="none"
          stroke={ringColor}
          strokeWidth={1}
          opacity={0.4}
        />
        <circle
          cx={cx}
          cy={cy}
          r={innerR}
          fill="none"
          stroke={ringColor}
          strokeWidth={1}
          opacity={0.25}
        />

        {/* Tick marks every 45° */}
        {ticks.map(({ deg, inner, outer }) => (
          <line
            key={deg}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke={ringColor}
            strokeWidth={1}
            opacity={0.5}
          />
        ))}

        {/* Diagonal angle labels (between rings) */}
        {diagLabels.map(({ deg, label, anchor }) => {
          const pos = pointAt(deg, diagLabelR)
          return (
            <text
              key={deg}
              x={pos.x}
              y={pos.y}
              textAnchor={anchor as "start" | "middle" | "end"}
              dominantBaseline="middle"
              fill={labelColor}
              fontSize={10}
              fontFamily="inherit"
              opacity={0.7}
            >
              {label}
            </text>
          )
        })}

        {/* Cardinal labels (outside ring) */}
        {cardinals.map(({ deg, nameKey, angleLabel, anchor, dy }) => {
          const pos = pointAt(deg, cardinalOffset)
          const isVertical = deg === 0 || deg === 180
          return (
            <text
              key={deg}
              x={pos.x + (isVertical ? 0 : dy)}
              y={pos.y + (isVertical ? dy : 0)}
              textAnchor={anchor as "start" | "middle" | "end"}
              dominantBaseline="middle"
              fill={labelColor}
              fontSize={11}
              fontFamily="inherit"
              fontWeight={700}
            >
              <tspan x={pos.x + (isVertical ? 0 : dy)} dy="-6">
                {t(`plots_descriptions.DEVICE_POSITION.${nameKey}`)}
              </tspan>
              <tspan x={pos.x + (isVertical ? 0 : dy)} dy="13" fontSize={9} opacity={0.7}>
                {angleLabel}
              </tspan>
            </text>
          )
        })}

        {/* Reference line center → Front (0°/top) */}
        <line
          x1={cx}
          y1={cy}
          x2={axisTop.x}
          y2={axisTop.y}
          stroke={ringColor}
          strokeWidth={1}
          opacity={0.5}
          strokeDasharray="3 2"
        />

        {/* Arrow */}
        <line
          x1={cx}
          y1={cy}
          x2={tip.x}
          y2={tip.y}
          stroke={accentColor}
          strokeWidth={2}
          strokeLinecap="round"
          markerEnd="url(#compass-arrowhead)"
        />

        {/* Center dot */}
        <circle cx={cx} cy={cy} r={3} fill={accentColor} />
      </svg>

      <Typography
        sx={{
          fontSize: "1.8rem",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}
      >
        {`${Math.round(heading)}°`}
      </Typography>
    </Box>
  )
}

export default HeadingCompass
